import React from "react";
import Tab from "./Tab";
/* DataTable — Phase B primitive (refreshed in Phase 2d Rich).
   Sticky header pinned BELOW topbar (fixes legacy z-index divergence between
   .forecast and .deals-table). Density compact (36h rows).

   This is a LIGHT wrapper: the caller composes <thead>/<tbody>/<tr>/<td> as
   usual. The wrapper enforces sticky positioning, density tokens, row hover
   + focus rings, and consistent visual treatment.

   For complex tables with custom logic (forecast cell editing, deals filtering)
   the caller renders directly inside <Table> + uses these slot conventions.

   Props:
     density:       "compact" (default, 36h) | "comfortable" (48h)
     stickyHeader:  bool (default: true)
     topOffset:     CSS dimension — sticky top offset (default: var(--topbar-h))
     className

   Phase 2d Rich:
     - Row hover/focus rings handled in CSS (focus-visible draws an inset
       accent box when <tr> is tabindex=0).
     - <DataTable.DensityToggle> companion renders a 2-state segmented
       control wired to the caller's density state. Caller persists to
       localStorage if desired (the primitive is unopinionated).
*/

export type DataTableDensity = "compact" | "comfortable";

export interface DataTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  density?: DataTableDensity;
  stickyHeader?: boolean;
  topOffset?: string;
}

interface DataTableComponent extends React.FC<DataTableProps> {
  DensityToggle: React.FC<DensityToggleProps>;
}

const DataTable: DataTableComponent = ({
  density = "compact",
  stickyHeader = true,
  topOffset,
  className = "",
  children,
  ...rest
}) => {
  const cls = [
    "v2-table",
    `v2-table--${density}`,
    stickyHeader ? "v2-table--sticky" : "",
    className,
  ].filter(Boolean).join(" ");
  const style = topOffset
    ? ({ "--v2-table-sticky-top": topOffset } as React.CSSProperties)
    : undefined;
  return (
    <table className={cls} style={style} {...rest}>
      {children}
    </table>
  );
};

export interface DensityToggleProps {
  value: DataTableDensity;
  onChange: (next: DataTableDensity) => void;
  /** Optional label prefix shown to the left ("Density" by default). */
  label?: React.ReactNode | null;
  className?: string;
}

DataTable.DensityToggle = function DensityToggle({
  value,
  onChange,
  label = "Density",
  className = "",
}: DensityToggleProps) {
  return (
    <div className={`v2-table-density ${className}`.trim()}>
      {label != null ? <span className="v2-table-density__label">{label}</span> : null}
      <Tab
        size="sm"
        value={value}
        options={[
          { id: "compact",     label: "Compact" },
          { id: "comfortable", label: "Comfortable" },
        ]}
        onChange={(next: any) => onChange(next as DataTableDensity)}
        ariaLabel="Table density"
      />
    </div>
  );
};

export default DataTable;
